// src/contexts/ConfigContext.jsx
import { createContext, useContext, useMemo, useEffect } from 'react';

const ConfigContext = createContext(null);

export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error('useConfig must be used within ConfigProvider');
    }
    return context;
};

export const ConfigProvider = ({ 
    children, 
    globalConfig,
    shortcodeData,
    fallback = {}
}) => {
    // Extract all static configuration
    const {
        // Main config sections
        styleOptions = {},
        surfside = {},
        analytics = {},
        filterTerms = {},
        menuConfig = {},
        menuFilter = {},
        retailers = {},
        nonHeadlessRetailers = [],
        singleProductOptions = {},
        allRetailersConfig = {},
        locationGateConfig = {},
        tagSlugs = {},
        themeConfig = {},
        debug_mode = false,
        alternateMenuUrl = '/backup-menu/',
    } = globalConfig || {};
    
    // Computed static values
    const isSingleRetailer = useMemo(() => 
        Object.keys(retailers).length === 1, 
        [retailers]
    );
    
    const singleRetailerId = useMemo(() =>
        isSingleRetailer ? Object.keys(retailers)[0] : null,
        [isSingleRetailer, retailers]
    );

    // Populate global retailers store for Apollo Client (multi-key support)
    useEffect(() => {
        if (retailers && Object.keys(retailers).length > 0) {
            window.rangeHeadlessRetailers = retailers;
            console.log(`🔑 ConfigContext: Updated global retailers store with ${Object.keys(retailers).length} retailers`);

            // Log any retailers with keyIndex for debugging multi-key
            const retailersWithKeyIndex = Object.values(retailers).filter(r => r.keyIndex !== undefined);
            if (retailersWithKeyIndex.length > 0) {
                console.log(`🔑 ConfigContext: Found ${retailersWithKeyIndex.length} retailers with keyIndex:`,
                    retailersWithKeyIndex.map(r => ({ id: r.id, keyIndex: r.keyIndex, name: r.niceName || r.retailerName }))
                );
            }
        }
    }, [retailers]);

    const contextValue = useMemo(() => ({
        // All static config values
        styleOptions,
        themeConfig,
        surfside,
        analytics,
        debug_mode,
        alternateMenuUrl,
        filterTerms,
        menuFilter,
        tagSlugs,
        menuConfig,
        singleProductOptions,
        retailers,
        nonHeadlessRetailers,
        allRetailersConfig,
        locationGateConfig,
        shortcodeData,
        
        // Computed static values
        isSingleRetailer,
        singleRetailerId,
        fallback,
        
        // Helper functions for static data
        getRetailerById: (id) => retailers[id] || null,
        getRetailerBySlug: (slug) => Object.values(retailers).find(r => r.menuSlug === slug) || null,
    }), [
        styleOptions,
        themeConfig,
        surfside,
        analytics,
        debug_mode,
        alternateMenuUrl,
        filterTerms,
        menuFilter,
        tagSlugs,
        menuConfig,
        singleProductOptions,
        retailers,
        nonHeadlessRetailers,
        allRetailersConfig,
        locationGateConfig,
        shortcodeData,
        isSingleRetailer,
        singleRetailerId,
        fallback
    ]);

    return (
        <ConfigContext.Provider value={contextValue}>
            {children}
        </ConfigContext.Provider>
    );
};

export default ConfigContext;
